import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from './ui/card';
import { Button } from './ui/button';
import {
  Plus,
  Search,
  RefreshCw,
  Warehouse,
  MapPin,
  Check,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  Box,
  TextField,
  Modal,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Paper,
  Divider,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Delete, Edit, Close } from '@mui/icons-material';
import { getWarehouses, getClientLocations, getAccounts } from '../api';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`location-tabpanel-${index}`}
      aria-labelledby={`location-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const LocationsManagement = () => {
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  
  // Datos para ambos tipos de ubicaciones
  const [warehouseLocations, setWarehouseLocations] = useState([]); // Bodegas
  const [destinationLocations, setDestinationLocations] = useState([]); // Ubicaciones
  
  // Para el modal de nueva ubicación
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [formError, setFormError] = useState('');
  
  // Estado del formulario para bodega (warehouse)
  const [newWarehouse, setNewWarehouse] = useState({
    id: null,
    nombre: '',
    estado: '',
    ciudad_municipio: '',
    colonia: '',
    codigo_postal: '',
    direccion: '',
    prefijo: '',
    responsable: '',
    correo_electronico: '',
    telefono_fijo: '',
    tipo_de_bodega: 'ALMACEN', // Valor por defecto, no se muestra en el formulario
    rfc: '',
  });

  // Estado del formulario para ubicación (destination)
  const [newDestination, setNewDestination] = useState({
    alias: '',
    address: '',
    City: '',
    State: '',
    codigo_Postal: '',
    customer: '',
  });
  
  // Columnas para la tabla de almacenes (bodegas)
  const warehouseColumns = [
    { field: 'nombre', headerName: 'Nombre', flex: 1 },
    { field: 'direccion', headerName: 'Dirección', flex: 2 },
    { field: 'ciudad_municipio', headerName: 'Ciudad', flex: 1 },
    { field: 'estado', headerName: 'Estado', flex: 1 },
    { field: 'tipo_de_bodega', headerName: 'Tipo', flex: 1 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      renderCell: (params) => (
        <div className="flex gap-2">
          <IconButton 
            size="small" 
            color="primary" 
            onClick={() => handleEditWarehouse(params.row)}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            color="error" 
            onClick={() => handleDeleteWarehouse(params.row.id)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </div>
      ),
    },
  ];
  
  // Columnas para la tabla de destinos (ubicaciones)
  const destinationColumns = [
    { field: 'alias', headerName: 'Alias', flex: 1 },
    { field: 'address', headerName: 'Dirección', flex: 2 },
    { field: 'City', headerName: 'Ciudad', flex: 1 },
    { field: 'State', headerName: 'Estado', flex: 1 },
    { field: 'customer', headerName: 'Cliente', flex: 1 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      renderCell: (params) => (
        <div className="flex gap-2">
          <IconButton 
            size="small" 
            color="primary" 
            onClick={() => handleEditDestination(params.row)}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            color="error" 
            onClick={() => handleDeleteDestination(params.row.id)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </div>
      ),
    },
  ];
  
  // Cargar ubicaciones y clientes al inicio
  useEffect(() => {
    fetchLocations();
    fetchClients();
    fetchAccounts();
  }, []);
  
  const fetchLocations = async () => {
    setLoading(true);
    try {
      // Obtener bodegas (almacenes de carga)
      const warehouseData = await getWarehouses();
      setWarehouseLocations(warehouseData);
      
      // Obtener ubicaciones (destinos)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/clients/`);
      if (!response.ok) throw new Error('Error al cargar ubicaciones');
      const destinationData = await response.json();
      setDestinationLocations(destinationData);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Error al cargar ubicaciones');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const clientData = await getClientLocations();
      // Extraer nombres únicos de clientes
      const uniqueClients = [...new Set(clientData.map(client => client.customer))];
      setClients(uniqueClients);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Error al cargar clientes');
    }
  };

  const fetchAccounts = async () => {
    try {
      const accountData = await getAccounts();
      setAccounts(accountData);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error('Error al cargar cuentas');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
    setIsEditing(false);
    setFormError('');

    if (tabValue === 0) {
      // Modal para bodega (warehouse)
      setNewWarehouse({
        id: null,
        nombre: '',
        estado: '',
        ciudad_municipio: '',
        colonia: '',
        codigo_postal: '',
        direccion: '',
        prefijo: '',
        responsable: '',
        correo_electronico: '',
        telefono_fijo: '',
        tipo_de_bodega: 'ALMACEN', // Valor por defecto, no se muestra en el formulario
        rfc: '',
      });
    } else {
      // Modal para ubicación (destination)
      setNewDestination({
        alias: '',
        address: '',
        City: '',
        State: '',
        codigo_Postal: '',
        customer: '',
      });
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setIsEditing(false);
    setEditingLocationId(null);
    setSelectedClient('');
  };

  const handleEditWarehouse = (warehouse) => {
    setIsEditing(true);
    setEditingLocationId(warehouse.id);
    setNewWarehouse({
      id: warehouse.id || null,
      nombre: warehouse.nombre || '',
      estado: warehouse.estado || '',
      ciudad_municipio: warehouse.ciudad_municipio || '',
      colonia: warehouse.colonia || '',
      codigo_postal: warehouse.codigo_postal || '',
      direccion: warehouse.direccion || '',
      prefijo: warehouse.prefijo || '',
      responsable: warehouse.responsable || '',
      correo_electronico: warehouse.correo_electronico || '',
      telefono_fijo: warehouse.telefono_fijo || '',
      tipo_de_bodega: 'ALMACEN', // Valor por defecto, no se muestra en el formulario
      rfc: warehouse.rfc || '',
    });
    setOpenModal(true);
  };

  const handleEditDestination = (destination) => {
    setIsEditing(true);
    setEditingLocationId(destination.id);
    setNewDestination({
      alias: destination.alias || '',
      address: destination.address || '',
      City: destination.City || '',
      State: destination.State || '',
      codigo_Postal: destination.codigo_Postal || '',
      customer: destination.customer || '',
    });
    setSelectedClient(destination.customer);
    setOpenModal(true);
  };

  const handleDeleteWarehouse = async (warehouseId) => {
    if (!window.confirm('¿Está seguro de eliminar este almacén?')) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/bodegas/${warehouseId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Error al eliminar el almacén');
      
      toast.success('Almacén eliminado correctamente');
      fetchLocations(); // Recargar datos
    } catch (error) {
      console.error('Error deleting warehouse:', error);
      toast.error('Error al eliminar el almacén');
    }
  };

  const handleDeleteDestination = async (destinationId) => {
    if (!window.confirm('¿Está seguro de eliminar este destino?')) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/ubicaciones/${destinationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Error al eliminar el destino');
      
      toast.success('Destino eliminado correctamente');
      fetchLocations(); // Recargar datos
    } catch (error) {
      console.error('Error deleting destination:', error);
      toast.error('Error al eliminar el destino');
    }
  };

  const validateWarehouseForm = () => {
    if (!newWarehouse.nombre) return 'El nombre es obligatorio';
    if (!newWarehouse.direccion) return 'La dirección es obligatoria';
    if (!newWarehouse.ciudad_municipio) return 'La ciudad es obligatoria';
    if (!newWarehouse.estado) return 'El estado es obligatorio';
    return '';
  };

  const validateDestinationForm = () => {
    if (!newDestination.alias) return 'El alias es obligatorio';
    if (!newDestination.address) return 'La dirección es obligatoria';
    if (!newDestination.City) return 'La ciudad es obligatoria';
    if (!newDestination.State) return 'El estado es obligatorio';
    if (!newDestination.customer) return 'El cliente es obligatorio';
    return '';
  };

  const handleWarehouseInputChange = (e) => {
    const { name, value } = e.target;
    setNewWarehouse(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDestinationInputChange = (e) => {
    const { name, value } = e.target;
    setNewDestination(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClientChange = (e) => {
    const customerValue = e.target.value;
    setSelectedClient(customerValue);
    setNewDestination(prev => ({
      ...prev,
      customer: customerValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let error = '';
    let url = '';
    let method = '';
    let payload = {};
    
    setLoading(true);
    
    try {
      if (tabValue === 0) {
        // Formulario de bodega (warehouse)
        error = validateWarehouseForm();
        if (error) {
          setFormError(error);
          setLoading(false);
          return;
        }
        
        url = isEditing 
          ? `${process.env.REACT_APP_API_URL}/api/v1/bodegas/${editingLocationId}` 
          : `${process.env.REACT_APP_API_URL}/api/v1/bodegas/`;
        
        method = isEditing ? 'PUT' : 'POST';
        payload = newWarehouse;
      } else {
        // Formulario de ubicación (destination)
        error = validateDestinationForm();
        if (error) {
          setFormError(error);
          setLoading(false);
          return;
        }
        
        url = isEditing 
          ? `${process.env.REACT_APP_API_URL}/api/v1/ubicaciones/${editingLocationId}` 
          : `${process.env.REACT_APP_API_URL}/api/v1/ubicaciones/`;
        
        method = isEditing ? 'PUT' : 'POST';
        payload = newDestination;
      }
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) throw new Error('Error al procesar la ubicación');
      
      const successMessage = tabValue === 0 
        ? (isEditing ? 'Almacén actualizado correctamente' : 'Almacén añadido correctamente')
        : (isEditing ? 'Destino actualizado correctamente' : 'Destino añadido correctamente');
      
      toast.success(successMessage);
      handleCloseModal();
      fetchLocations(); // Recargar datos
    } catch (error) {
      console.error('Error saving location:', error);
      const errorMessage = tabValue === 0
        ? (isEditing ? 'Error al actualizar el almacén' : 'Error al añadir el almacén')
        : (isEditing ? 'Error al actualizar el destino' : 'Error al añadir el destino');
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Filtrar ubicaciones según la búsqueda
  const filteredWarehouseLocations = warehouseLocations.filter(location =>
    location.nombre?.toLowerCase().includes(searchValue.toLowerCase()) ||
    location.direccion?.toLowerCase().includes(searchValue.toLowerCase()) ||
    location.ciudad_municipio?.toLowerCase().includes(searchValue.toLowerCase()) ||
    location.estado?.toLowerCase().includes(searchValue.toLowerCase())
  );
  
  const filteredDestinationLocations = destinationLocations.filter(location =>
    location.alias?.toLowerCase().includes(searchValue.toLowerCase()) ||
    location.address?.toLowerCase().includes(searchValue.toLowerCase()) ||
    location.City?.toLowerCase().includes(searchValue.toLowerCase()) ||
    location.State?.toLowerCase().includes(searchValue.toLowerCase()) ||
    location.customer?.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Renderizar el formulario según la pestaña activa
  const renderForm = () => {
    if (tabValue === 0) {
      // Formulario de bodega (warehouse)
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Nombre"
            name="nombre"
            value={newWarehouse.nombre}
            onChange={handleWarehouseInputChange}
            fullWidth
            required
          />
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Estado"
              name="estado"
              value={newWarehouse.estado}
              onChange={handleWarehouseInputChange}
              fullWidth
              required
            />
            
            <TextField
              label="Ciudad"
              name="ciudad_municipio"
              value={newWarehouse.ciudad_municipio}
              onChange={handleWarehouseInputChange}
              fullWidth
              required
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Colonia"
              name="colonia"
              value={newWarehouse.colonia}
              onChange={handleWarehouseInputChange}
              fullWidth
            />
            
            <TextField
              label="Código Postal"
              name="codigo_postal"
              value={newWarehouse.codigo_postal}
              onChange={handleWarehouseInputChange}
              fullWidth
            />
          </Box>
          
          <TextField
            label="Dirección"
            name="direccion"
            value={newWarehouse.direccion}
            onChange={handleWarehouseInputChange}
            fullWidth
            required
          />
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Responsable"
              name="responsable"
              value={newWarehouse.responsable}
              onChange={handleWarehouseInputChange}
              fullWidth
            />
            
            <TextField
              label="Correo electrónico"
              name="correo_electronico"
              value={newWarehouse.correo_electronico}
              onChange={handleWarehouseInputChange}
              fullWidth
              type="email"
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Teléfono fijo"
              name="telefono_fijo"
              value={newWarehouse.telefono_fijo}
              onChange={handleWarehouseInputChange}
              fullWidth
            />
            
            <TextField
              label="RFC"
              name="rfc"
              value={newWarehouse.rfc}
              onChange={handleWarehouseInputChange}
              fullWidth
            />
          </Box>
          
          <TextField
            label="Prefijo"
            name="prefijo"
            value={newWarehouse.prefijo}
            onChange={handleWarehouseInputChange}
            fullWidth
          />
        </Box>
      );
    } else {
      // Formulario de ubicación (destination)
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Alias"
            name="alias"
            value={newDestination.alias}
            onChange={handleDestinationInputChange}
            fullWidth
            required
          />

          <FormControl fullWidth required>
            <InputLabel id="client-label">Cliente</InputLabel>
            <Select
              labelId="client-label"
              id="client"
              value={selectedClient}
              label="Cliente"
              onChange={handleClientChange}
            >
              {clients.map((client) => (
                <MenuItem key={client} value={client}>
                  {client}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Dirección"
            name="address"
            value={newDestination.address}
            onChange={handleDestinationInputChange}
            fullWidth
            required
          />
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Ciudad"
              name="City"
              value={newDestination.City}
              onChange={handleDestinationInputChange}
              fullWidth
              required
            />
            
            <TextField
              label="Estado"
              name="State"
              value={newDestination.State}
              onChange={handleDestinationInputChange}
              fullWidth
              required
            />
          </Box>
          
          <TextField
            label="Código Postal"
            name="codigo_Postal"
            value={newDestination.codigo_Postal}
            onChange={handleDestinationInputChange}
            fullWidth
          />
        </Box>
      );
    }
  };

  return (
    <Card className="shadow-lg hover:shadow-xl transition-all duration-300 mt-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-medium">Gestión de Ubicaciones</CardTitle>
        <div className="flex space-x-2">
          <div className="relative">
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Buscar ubicaciones..."
              className="pl-10 pr-4 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none transition-colors duration-200 w-64"
            />
            <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
          </div>
          <Button 
            onClick={fetchLocations}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800"
            title="Refrescar lista"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button 
            onClick={handleOpenModal}
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            <Plus className="h-4 w-4 mr-2" /> {tabValue === 0 ? 'Nuevo Almacén' : 'Nuevo Destino'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="location tabs">
              <Tab 
                label={
                  <div className="flex items-center gap-2">
                    <Warehouse className="h-4 w-4" /> 
                    <span>Almacenes de carga</span>
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded">
                      {warehouseLocations.length}
                    </span>
                  </div>
                } 
                id="location-tab-0" 
                aria-controls="location-tabpanel-0" 
              />
              <Tab 
                label={
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" /> 
                    <span>Destinos</span>
                    <span className="bg-pink-100 text-pink-800 text-xs font-medium px-2 py-0.5 rounded">
                      {destinationLocations.length}
                    </span>
                  </div>
                } 
                id="location-tab-1" 
                aria-controls="location-tabpanel-1" 
              />
            </Tabs>
          </Box>
          <TabPanel value={tabValue} index={0}>
            <Paper elevation={0} sx={{ height: 400, width: '100%' }}>
              <DataGrid
                rows={filteredWarehouseLocations}
                columns={warehouseColumns}
                pageSize={5}
                rowsPerPageOptions={[5]}
                disableSelectionOnClick
                loading={loading}
                getRowId={(row) => row.id}
              />
            </Paper>
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <Paper elevation={0} sx={{ height: 400, width: '100%' }}>
              <DataGrid
                rows={filteredDestinationLocations}
                columns={destinationColumns}
                pageSize={5}
                rowsPerPageOptions={[5]}
                disableSelectionOnClick
                loading={loading}
                getRowId={(row) => row.id}
              />
            </Paper>
          </TabPanel>
        </Box>
      </CardContent>

      {/* Modal para añadir/editar ubicación */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-title"
      >
        <Box sx={style}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography id="modal-title" variant="h6" component="h2">
              {isEditing 
                ? (tabValue === 0 ? 'Editar almacén' : 'Editar destino')
                : (tabValue === 0 ? 'Nuevo almacén' : 'Nuevo destino')
              }
            </Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3 }} />
          
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            {renderForm()}
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button
                onClick={handleCloseModal}
                variant="outlined"
              >
                Cancelar
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                className="bg-blue-500 hover:bg-blue-600"
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : isEditing ? (
                  'Actualizar'
                ) : (
                  'Guardar'
                )}
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>
    </Card>
  );
};

export default LocationsManagement;